// Player-join form: name input (or Twitch branching), submit button, and disabled/label state.

import React from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/custom/Icon';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/utilities/cn';
import { getCharCounterClass } from '../../utilities/inputFeedback';
import { useValidatedInput } from '../../hooks/useValidatedInput';
import { useRoomConfig } from '../../hooks/useRoomConfig';
import { useRoomMyPlayer } from '../../hooks/useRoomMyPlayer';
import { useRoomState } from '../../hooks/useRoomState';
import { useRoomStateSelector } from '../../hooks/useRoomStateSelector';
import { getRoomCapacityRemaining } from '@airhead-games/shared/engine/roomState';

const PLAYER_NAME_MAX = 12;

interface PlayerJoinControlsProps {
  isJoining: boolean;
  onSubmit: (playerName: string) => Promise<void | false>;
  onTwitchAuthClick: () => void;
}

const PlayerJoinControls: React.FC<PlayerJoinControlsProps> = ({
  isJoining,
  onSubmit,
  onTwitchAuthClick,
}) => {
  const { twitchPlayersOnly, disallowBlockedAndBanned, blockedTwitchUserIds } =
    useRoomConfig();
  const myPlayer = useRoomMyPlayer();
  const { playerInitializationError: initializationError } = useRoomState();
  const hasRoomCapacity = useRoomStateSelector((state) =>
    state ? getRoomCapacityRemaining(state) > 0 : true
  );

  const hasTwitchAuth = !!twitchPlayersOnly && !!myPlayer?.hasTwitchAuth;
  const isBlocked =
    !!twitchPlayersOnly &&
    !!disallowBlockedAndBanned &&
    !!myPlayer?.twitchUserId &&
    Array.isArray(blockedTwitchUserIds) &&
    blockedTwitchUserIds.includes(myPlayer.twitchUserId);

  // Form-level disabled state from room/network conditions. Composed below
  // with the hook's own isSubmitting via OR — either one disables the input.
  const isFormDisabled = !hasRoomCapacity || isJoining || !!initializationError;

  const buttonLabel = React.useMemo(() => {
    if (isJoining) return 'Joining...';
    if (!hasRoomCapacity) return 'Room is full';
    if (initializationError) return 'Error loading player';
    return 'Confirm player';
  }, [hasRoomCapacity, initializationError, isJoining]);

  React.useEffect(() => {
    if (isBlocked) {
      toast.error('You are not allowed to join this room');
    }
  }, [isBlocked]);

  const name = useValidatedInput({
    initialValue: myPlayer?.playerName,
    rules: [
      `maxLength:${PLAYER_NAME_MAX}`,
      'noRepeatedChars',
      'noPunctuationExceptName',
      'noSymbols',
      'noControl',
      'noMarks',
      'noReservedHostName',
    ],
    checkModCensors: true,
    transforms: ['capitalizeFirst'],
    onSubmit,
  });

  const twitchHandleButton = (
    <Button
      type="button"
      variant="twitch"
      size="xl"
      disabled
      className="w-full"
    >
      <Icon name="twitch" size={20} color="currentColor" />
      {myPlayer?.playerName}
    </Button>
  );

  if (twitchPlayersOnly) {
    if (!hasTwitchAuth) {
      return (
        <>
          <div aria-hidden className="h-3" />
          <Button
            type="button"
            variant="twitch"
            size="xl"
            className="w-full cursor-pointer"
            onClick={onTwitchAuthClick}
          >
            <Icon name="twitch" size={20} color="currentColor" />
            Join with Twitch
          </Button>
        </>
      );
    }
    if (isBlocked) {
      return (
        <>
          <div aria-hidden className="h-3" />
          {twitchHandleButton}
        </>
      );
    }
    return (
      <>
        <div aria-hidden className="h-3" />
        {twitchHandleButton}
        <Button
          type="button"
          size="xl"
          className="w-full cursor-pointer"
          disabled={isFormDisabled}
          onClick={() => void onSubmit(myPlayer?.playerName || '')}
        >
          {isJoining ? <Spinner /> : buttonLabel}
        </Button>
      </>
    );
  }

  return (
    <form
      className="w-full"
      onSubmit={(event) => {
        event.preventDefault();
        void name.submit();
      }}
    >
      <FieldGroup>
        <Field data-invalid={!!name.error}>
          <div className="flex w-full items-center justify-between gap-3">
            <span aria-hidden className="min-w-0 shrink-0" />
            <div className="text-xs leading-none">
              <span
                className={cn(
                  getCharCounterClass(name.value.length, PLAYER_NAME_MAX)
                )}
              >
                {name.value.length}/{PLAYER_NAME_MAX}
              </span>
            </div>
          </div>
          <Input
            {...name.inputProps}
            disabled={isFormDisabled || name.inputProps.disabled}
            placeholder="Enter Name"
            autoCapitalize="sentences"
            maxLength={PLAYER_NAME_MAX}
            aria-invalid={!!name.error}
            className="h-11 text-center text-base md:text-base"
          />
          {name.error ? <FieldError>{name.error}</FieldError> : null}
        </Field>
        <Button
          type="submit"
          size="xl"
          className="w-full cursor-pointer"
          disabled={isFormDisabled || name.isSubmitting}
        >
          {name.isSubmitting || isJoining ? <Spinner /> : buttonLabel}
        </Button>
      </FieldGroup>
    </form>
  );
};

export default PlayerJoinControls;
